import { useData } from "data/data-provider";
import Invoice from "designs/Common/(car-search-form)/invoice";
import LongTermRentalInvoice, {
  hasExpensiveCycles,
} from "designs/Common/(car-search-form)/LongTermInvoice";
import moment from "moment";
import React from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";

function LongTermRentalUI({
  handleBooking,
  loading,
  loadingLater,
  handleBookingLater,
  renterDetailsConfirmed,
}: any) {
  const {
    selectedCar,
    startDate,
    endDate,
    pickupLocation,
    invoice,
    dropOffLocation,
    dropoffLocationType,
    ourCompany,
    long_term_rental_nature,
    long_term_rental_duration,
    weeklyQuote,
    acknowledgedExpensiveCycles,
    setAcknowledgedExpensiveCycles,
  } = useData();

  // 1NOW-1095: gate proceed-to-pay when any projected cycle exceeds Week 1
  // by >25% — the renter must explicitly acknowledge the escalating totals
  // before we ship them to checkout, mirroring the Bonzah acknowledgement.
  const needsExpensiveAck = hasExpensiveCycles(weeklyQuote);
  const ackBlocking = needsExpensiveAck && !acknowledgedExpensiveCycles;

  return (
    <>
      <div className="listingSectionSidebar__wrap shadow-xl mt-4 lg:mt-0 p-6 lg:p-10 lg:max-w-xl  ">
        {pickupLocation && (
          <>
            {" "}
            <h2 className="text-2xl font-bold text-text-color mb-3">
              Booking Details
            </h2>
            <div className="mt-8 flex">
              <div className="flex-shrink-0 flex flex-col items-center py-2">
                <span className="block w-6 h-6 rounded-full border border-secondary-100"></span>
                <span className="block flex-grow border-l border-secondary-100 border-dashed my-1"></span>
                <span className="block w-6 h-6 rounded-full border border-secondary-100"></span>
              </div>
              <div className="ml-4 space-y-14 text-sm">
                <div className="flex flex-col space-y-2">
                  <span className=" text-text-color ">Pick up</span>
                  <span className="text-text-color font-semibold">
                    {" "}
                    {pickupLocation?.name}
                  </span>
                  {startDate && (
                    <span className=" font-semibold text-text-color !mt-0">
                      {moment(startDate).format("ddd, D MMM YYYY")}{" "}
                      {ourCompany?.default_time_format === "24_hour"
                        ? moment(startDate).format("H:mm")
                        : moment(startDate).format("h:mm A")}
                    </span>
                  )}
                </div>
                <div className="flex flex-col space-y-2">
                  <span className=" text-text-color ">Drop Off</span>
                  <span className="text-text-color font-semibold">
                    {dropoffLocationType === false && dropOffLocation?.name
                      ? dropOffLocation?.name
                      : pickupLocation?.name}
                  </span>
                  {endDate && (
                    <span className=" font-semibold text-text-color !mt-0">
                      {moment(endDate).format("ddd, D MMM YYYY")}{" "}
                      {ourCompany?.default_time_format === "24_hour"
                        ? moment(endDate).format("H:mm")
                        : moment(endDate).format("h:mm A")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="listingSectionSidebar__wrap shadow-xl mt-5 p-6 lg:p-10 lg:max-w-xl  ">
        <LongTermRentalInvoice
          long_term_rental_nature={long_term_rental_nature}
          long_term_rental_duration={long_term_rental_duration}
          invoice={invoice}
          startDate={startDate}
          carName={selectedCar?.name}
          weeklyQuote={weeklyQuote}
        />
        {needsExpensiveAck && (
          <div className="mt-4 rounded-lg border border-orange-400 bg-orange-50 p-3 text-sm text-text-color">
            <p className="font-semibold text-orange-700">
              Heads up — later weeks cost more than Week 1
            </p>
            <p className="mt-1">
              The fleet has a custom-price calendar that makes some upcoming
              {long_term_rental_nature === "monthly" ? " months " : " weeks "}
              cost more than 25% above Week 1. You will be auto-charged each
              {long_term_rental_nature === "monthly" ? " month's " : " week's "}
              amount as it starts.
            </p>
            <label className="mt-3 flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={acknowledgedExpensiveCycles}
                onChange={(e) =>
                  setAcknowledgedExpensiveCycles(e.target.checked)
                }
              />
              <span>
                I understand each
                {long_term_rental_nature === "monthly" ? " month " : " week "}
                will be charged at the amount shown above.
              </span>
            </label>
          </div>
        )}
      </div>
      <div className="listingSectionSidebar__wrap shadow-xl mt-5 p-6 lg:p-10 lg:max-w-xl mb-10">
        <h2 className="text-2xl font-bold text-text-color">
          {long_term_rental_nature === "weekly"
            ? "Weekly Invoice"
            : "Monthly Invoice"}
        </h2>

        <div className="px-4 border-t border-secondary-100">
          <Invoice invoice={invoice} carName={selectedCar?.name} />
        </div>

        <ButtonPrimary
          className="block rounded-3xl"
          disabled={loading || ackBlocking}
          onClick={() => {
            handleBooking();
          }}
        >
          {loading ? (
            <div className="flex justify-center items-center col-span-full">
              <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-secondary-100"></div>
            </div>
          ) : (
            "Pay & Book"
          )}
        </ButtonPrimary>
        {selectedCar?.pay_later && (
          <ButtonPrimary
            className="block rounded-lg mt-4"
            disabled={loadingLater || ackBlocking}
            onClick={() => {
              handleBookingLater();
            }}
          >
            {loadingLater ? (
              <div className="flex justify-center items-center col-span-full">
                <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
              </div>
            ) : (
              "Pay Later"
            )}
          </ButtonPrimary>
        )}
      </div>
    </>
  );
}

export default LongTermRentalUI;
